import { useState } from 'react';

import Box from '@mui/material/Box/Box';
import Stack from '@mui/material/Stack/Stack';
import { useTranslation } from 'react-i18next';
import { FormikProvider, useFormik } from 'formik';

import { getCardFormSchema } from '../../utils';
import { useAdyenCard } from '../../hooks/use-adyen-card';
import { Checkbox } from '../../../../../Checkbox/Checkbox';
import { InputField } from '../../../../../InputField/InputField';
import { useCheckout } from '../../../../../../hooks/use-checkout';
import { useAppConfig } from '../../../../../../hooks/use-app-config';
import { AddressElement } from '../../../AddressElement/AddressElement';
import { PayButton } from '../../../PaymentForm/components/PayButton/PayButton';
import { EMAIL_FIELD_DEFAULT_ATTRIBUTES } from '../../../../../../constants/consts';
import { AdyenThreeDSComponent } from '../AdyenThreeDSContainer/AdyenThreeDSContainer';
import { useAddressElementRef } from '../../../AddressElement/hooks/use-address-element-ref';
import { BackToSavedCardButton } from '../../../BackToSavedCardButton/BackToSavedCardButton';
import { useShowTaxCalculationFields } from '../../../../hooks/use-show-tax-calculation-fields';
import { PaymentElementContainer } from '../../../PaymentElementContainer/PaymentElementContainer';

import '@adyen/adyen-web/styles/adyen.css';
import './styles.scss';

interface AydenCardProps {
  savedCardProps: {
    defaultSaveCardChecked: boolean;
    onBackButtonClicked: () => void;
    showBackButton: boolean;
  };
}

export function AydenCard({ savedCardProps }: AydenCardProps) {
  const { cardComponentRef, handlePay, threeDSComponent } = useAdyenCard();
  const { useTaxCalculationField, showAddressField } = useShowTaxCalculationFields();
  const { AddressElementRef } = useAddressElementRef();
  const { featureFlags } = useAppConfig();
  const shouldHideEmailField = featureFlags?.checkout_enforce_customer_email;
  const shouldHideFieldsLabels = featureFlags?.checkout_remove_field_labels;
  const { checkoutData } = useCheckout();
  const showSaveMyCard = Boolean(checkoutData?.checkoutConfigurations?.showSaveMyCard);
  const [isSaveMyCardSelected, setIsSavedMyCardSelected] = useState(
    showSaveMyCard &&
      (savedCardProps.defaultSaveCardChecked ||
        Boolean(featureFlags?.checkout_select_save_card_by_default))
  );
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });

  const formik = useFormik({
    initialValues: {
      email: checkoutData?.customer?.email || '',
      cardholderName: '',
    },
    validateOnMount: true,
    validationSchema: getCardFormSchema(shouldHideEmailField),
    onSubmit: () => {},
  });

  const onPayClick = () => {
    formik.submitForm();
    AddressElementRef.current?.validate();
    const isAddressValid = !useTaxCalculationField || Boolean(AddressElementRef.current?.isValid());
    handlePay({
      email: formik.values.email,
      isUserDetailsValid: formik.isValid && isAddressValid,
      shouldSaveCard: isSaveMyCardSelected,
      cardholderName: formik.values.cardholderName,
    });
  };

  return (
    <PaymentElementContainer
      content={
        <Stack spacing={1.5}>
          {savedCardProps.showBackButton && (
            <BackToSavedCardButton onClick={savedCardProps.onBackButtonClicked} />
          )}
          <FormikProvider value={formik}>
            <InputField
              name="cardholderName"
              label={shouldHideFieldsLabels ? '' : t('cardholderName')}
              inputProps={{
                placeholder: t('cardholderName'),
              }}
            />
            {!shouldHideEmailField && (
              <InputField
                name="email"
                label={shouldHideFieldsLabels ? '' : t('email')}
                inputProps={{
                  placeholder: t('emailAddress'),
                  ...EMAIL_FIELD_DEFAULT_ATTRIBUTES,
                }}
              />
            )}
          </FormikProvider>
          <Box
            className={`custom-card ${shouldHideFieldsLabels ? 'hide-labels' : ''}`}
            ref={cardComponentRef}
          />
          {showSaveMyCard && (
            <Box pt={0.5}>
              <Checkbox
                label={t('saveCardCheckbox')}
                checkboxProps={{
                  onChange: (event) => {
                    setIsSavedMyCardSelected(event.target.checked);
                  },
                  checked: isSaveMyCardSelected,
                }}
              />
            </Box>
          )}
          {useTaxCalculationField && (
            <AddressElement ref={AddressElementRef} zipOnly={!showAddressField} />
          )}
        </Stack>
      }
    >
      <PayButton onClick={onPayClick} />
      <AdyenThreeDSComponent threeDSComponent={threeDSComponent} />
    </PaymentElementContainer>
  );
}
